import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Percent, Banknote, CalendarDays, Send, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: Package, title: "Free product", desc: "A starter pack on us — 2 × Breethe 30-strip packs posted to you. Try it properly before you talk about it." },
  { icon: Percent, title: "Your own code", desc: "A personal 10%-off code and trackable link for your audience — works on everything, even the $14.95 first pack." },
  { icon: Banknote, title: "15% commission", desc: "Of the product total on every sale your code brings in. Tracked live in your own ambassador portal." },
  { icon: CalendarDays, title: "30-day start", desc: "Every partnership starts as a 30-day trial. If it works for both of us, it keeps rolling — no lock-in either way." },
];

const STEPS = [
  ["Apply below", "Tell us who you are and where you post. Every application is read by a human — us."],
  ["We chat", "If it looks like a fit, we'll reach out, hear what you'd bring, and send your invite link."],
  ["Sign + start", "Sign the ambassador agreement online, get your starter pack, your code and your portal."],
  ["Share + earn", "Post genuinely, share your code, and watch sales and commission land in your portal."],
];

const LOOKING_FOR = [
  "Runners, gym-goers and health-conscious Aussies — engaged beats big. 1,500 locals who trust you outrank 30k strangers.",
  "Genuine comments and conversations on your posts, not just likes.",
  "Content we'd be proud to repost — real people, real use, no scripts.",
  "Consistency: you post and chat with your audience regularly.",
  "You're in it to promote something you rate — not just for free stuff.",
];

const WE_ASK = [
  "Over your first 30 days: one genuine Reel or TikTok using Breethe, plus 2–3 Stories.",
  "Tag @breetheau (Instagram) / @breethe.au (TikTok) and always disclose — #ad or the paid-partnership label.",
  "Honest opinions only. If you don't love the product, you tell us — you never owe us a positive post.",
];

const Ambassadors = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem("breethe-theme") || "dark");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [followers, setFollowers] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("ready"); // ready | sending | sent
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem("breethe-theme", theme);
  }, [theme]);

  const submit = async () => {
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (!instagram.trim() && !tiktok.trim()) {
      setError("Share at least one social handle so we can find you.");
      return;
    }
    setState("sending");
    try {
      await axios.post(`${API}/ambassadors/apply`, {
        name: name.trim(),
        email: email.trim(),
        instagram: instagram.trim(),
        tiktok: tiktok.trim(),
        followers,
        message: message.trim(),
      });
      setState("sent");
      window.scrollTo(0, 0);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong — try again.");
      setState("ready");
    }
  };

  const inputCls =
    "w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300";

  return (
    <div
      data-testid="ambassadors-page"
      className="App bg-[#0A0A0A] text-white font-manrope antialiased overflow-x-clip"
      data-theme={theme}
    >
      <div className="noise-overlay" aria-hidden="true" />
      <Navbar theme={theme} onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} />

      <main>
        {/* Hero */}
        <section className="px-6 md:px-10 pt-16 md:pt-24 pb-12 max-w-[1100px] mx-auto">
          <p className="font-plex text-xs uppercase tracking-[0.3em] text-volt">Breethe ambassadors</p>
          <h1 className="mt-4 font-anton uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.9]">
            Run with us<span className="text-volt">.</span>
            <span className="block">Earn with us<span className="text-volt">.</span></span>
          </h1>
          <p className="mt-6 text-white/60 font-light text-base md:text-lg max-w-2xl">
            We're building a small crew of Australian ambassadors who genuinely rate Breethe.
            This isn't a one-off sponsored post — it's a partnership where you earn on every
            sale you bring in, with free product, your own discount code and a portal to track it all.
          </p>
        </section>

        {/* Perks */}
        <section className="px-6 md:px-10 py-10 max-w-[1100px] mx-auto">
          <p className="font-plex text-xs uppercase tracking-[0.3em] text-white/40">What you get</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PERKS.map((p, i) => (
              <div key={p.title} data-testid={`program-perk-${i}`} className="border border-white/10 bg-[#121212] p-6">
                <p.icon size={22} className="text-volt" />
                <p className="mt-4 font-anton uppercase text-lg">{p.title}</p>
                <p className="mt-2 text-white/60 font-light text-sm md:text-base">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="px-6 md:px-10 py-10 max-w-[1100px] mx-auto">
          <p className="font-plex text-xs uppercase tracking-[0.3em] text-white/40">How it works</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {STEPS.map(([title, desc], i) => (
              <div key={title} data-testid={`program-step-${i}`} className="border-t-2 border-volt pt-4">
                <p className="font-anton text-volt text-3xl">0{i + 1}</p>
                <p className="mt-2 font-anton uppercase text-base">{title}</p>
                <p className="mt-2 text-white/60 font-light text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Looking for + we ask */}
        <section className="px-6 md:px-10 py-10 max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p className="font-plex text-xs uppercase tracking-[0.3em] text-white/40">Who we're after</p>
            <ul className="mt-5 space-y-4">
              {LOOKING_FOR.map((t, i) => (
                <li key={i} className="flex gap-3 text-white/70 font-light text-sm md:text-base">
                  <Check size={16} className="text-volt shrink-0 mt-1" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-plex text-xs uppercase tracking-[0.3em] text-white/40">What we ask</p>
            <ul className="mt-5 space-y-4">
              {WE_ASK.map((t, i) => (
                <li key={i} className="flex gap-3 text-white/70 font-light text-sm md:text-base">
                  <Check size={16} className="text-volt shrink-0 mt-1" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Application form */}
        <section className="px-6 md:px-10 py-12 max-w-[1100px] mx-auto">
          <div className="border border-white/10 bg-[#121212] p-6 md:p-10">
            {state === "sent" ? (
              <div data-testid="apply-success" className="text-center py-10">
                <p className="font-anton uppercase text-3xl md:text-5xl">
                  Application in<span className="text-volt">.</span>
                </p>
                <p className="mt-4 text-white/60 font-light max-w-md mx-auto">
                  Thanks {name.split(" ")[0]} — we read every application personally. If it looks
                  like a fit, you'll hear from us at {email} with your invite link.
                </p>
              </div>
            ) : (
              <>
                <p className="font-plex text-xs uppercase tracking-[0.3em] text-volt">Apply now</p>
                <h2 className="mt-3 font-anton uppercase text-2xl md:text-4xl">
                  Tell us about you<span className="text-volt">.</span>
                </h2>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="YOUR NAME" data-testid="apply-name-input" className={inputCls} />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="YOUR EMAIL" data-testid="apply-email-input" className={inputCls} />
                  <input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="INSTAGRAM — @HANDLE" data-testid="apply-instagram-input" className={inputCls} />
                  <input value={tiktok} onChange={(e) => setTiktok(e.target.value)} placeholder="TIKTOK — @HANDLE" data-testid="apply-tiktok-input" className={inputCls} />
                  <select
                    value={followers}
                    onChange={(e) => setFollowers(e.target.value)}
                    data-testid="apply-followers-select"
                    className={`${inputCls} appearance-none ${followers ? "" : "text-white/30"}`}
                  >
                    <option value="" className="bg-[#121212]">APPROX. FOLLOWERS</option>
                    <option value="under-1k" className="bg-[#121212]">Under 1,000</option>
                    <option value="1k-5k" className="bg-[#121212]">1,000 – 5,000</option>
                    <option value="5k-20k" className="bg-[#121212]">5,000 – 20,000</option>
                    <option value="20k-plus" className="bg-[#121212]">20,000+</option>
                  </select>
                  <div className="md:col-span-2">
                    <textarea
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="WHAT'S YOUR CONTENT ABOUT, AND WHY BREETHE? (OPTIONAL)"
                      data-testid="apply-message-input"
                      className="w-full bg-transparent border border-white/20 focus:border-volt px-4 py-3 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300"
                    />
                  </div>
                </div>
                {error && (
                  <p data-testid="apply-error" className="mt-4 font-plex text-xs uppercase tracking-[0.15em] text-volt">{error}</p>
                )}
                <button
                  onClick={submit}
                  disabled={state === "sending"}
                  data-testid="apply-submit-button"
                  className="mt-6 h-14 px-10 bg-volt text-black font-anton uppercase tracking-wider text-base flex items-center gap-3 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
                >
                  {state === "sending" ? "Sending…" : <>Send my application <Send size={16} /></>}
                </button>
                <p className="mt-4 text-white/30 font-light text-xs">
                  We review every application ourselves — no auto-accepts. Invites go out by email.
                </p>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Ambassadors;
